import React, { useState } from "react";
import { motion, AnimatePresence, stagger } from "motion/react";
import CollapsibleSection from "@/components/ProductPage/ProductPageHero/CollapsibleSection";
import { RichSBData, Resource } from "@/components/ProductPage/ProductPageHero/types";
import styled from "styled-components";
import Link from "next/link";
import Button from "@/components/Buttons/Button";
import { FileDown } from "lucide-react";
import { useFileSize } from "@/hooks/useFileSize";

export interface ProductPageResourcesProps {
  record: Record<string, unknown>;
  RichSBData: RichSBData;
}

const ResourceListContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 16px;
  padding-bottom: 0px;
`;

const ResourceItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  appearance: none;
  text-decoration: none;
  color: unset;
`;

const ResourceItemTitle = styled(motion.span)`
  color: #292929;
  font-family: Inter;
  font-size: 0.8rem;
  font-style: normal;
  font-weight: 500;
  line-height: 1.25rem;
`;

const containerVariants = {
  hidden: { x: 0, transition: { when: "beforeChildren", delayChildren: 0.5 } },
  visible: { x: 0, transition: { when: "beforeChildren", delayChildren: stagger(0.03, { from: "first" }) } },
};

const itemVariants = {
  hidden: { opacity: 0, y: -30, transition: { duration: 0.25, ease: [0.26, 1, 0.5, 1] } },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.26, 1, 0.5, 1] },
  },
};

const formatResourceLabel = (label: string, fileSize: string | null): string => {
  if (!fileSize) {
    return label;
  }
  return `${label} · ${fileSize}`;
};

interface ResourceItemProps {
  resource: Resource;
  index: number;
}

const ResourceItemWithSize = ({ resource, index }: ResourceItemProps) => {
  const { formattedSize } = useFileSize(resource.asset.filename);
  const formattedLabel = formatResourceLabel(resource.label, formattedSize);

  return (
    <ResourceItem variants={itemVariants as any}>
      <Button
        style={"plantTagResource"}
        label={formattedLabel}
        link={resource.asset.filename}
        customIcon={<FileDown />}
        iconPosition='left'
        contentWall={resource?.contentWall?.length > 0 && resource?.contentWall}
      />
    </ResourceItem>
  );
};

export default function ProductPageResources({ record, RichSBData }: ProductPageResourcesProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <CollapsibleSection
      label='Resources'
      isDefaultOpen
      hasCustomState
      isCustomOpen={isOpen}
      setIsCustomOpen={setIsOpen}>
      <ResourceListContainer
        variants={containerVariants as any}
        initial={isOpen ? "visible" : "hidden"}
        animate={isOpen ? "visible" : "hidden"}>
        {RichSBData?.content?.resources?.map((resource, i) => (
          <ResourceItemWithSize key={i} resource={resource} index={i} />
        ))}
      </ResourceListContainer>
    </CollapsibleSection>
  );
}
